import {DashCanvasItemState} from '@xh/hoist/desktop/cmp/dash';
import {ChartSpec, DEFAULT_CHART_SPEC} from '../../cmp/chartSpec/ChartSpecTypes';
import {DEFAULT_GRID_SPEC, FormatSpec, GridSpec, GridSpecColumn, GridSpecSorter} from '../../cmp/gridSpec/GridSpecTypes';
import {ResearchDataSource, ResearchWidgetKind, ResearchWidgetState} from '../../data/ResearchTypes';

/**
 * Ready-made research widgets: a data source plus a spec tuned by hand. They are only starting points -
 * once added, a preset is an ordinary grid or chart widget whose data and spec the user (or the AI)
 * can change. The default dashboard is laid out from them.
 */
export interface ResearchPreset {
    id: string;
    /** Heading in the "Add" menu. */
    group: string;
    title: string;
    kind: ResearchWidgetKind;
    state: ResearchWidgetState;
    /** Size when added, in canvas columns and rows. */
    width: number;
    height: number;
}

/** Size of a widget added without a preset. */
export const DEFAULT_WIDGET_SIZE = {width: 14, height: 12};

const MILLIONS: FormatSpec = {type: 'number', numberStyle: 'millions'},
    PER_SHARE: FormatSpec = {type: 'number', numberStyle: 'price', precision: 2, prefix: '$'},
    YEAR: FormatSpec = {type: 'date', dateFmt: 'YYYY'},
    NEWEST_FIRST: GridSpecSorter[] = [{colId: 'date', sort: 'desc'}];

const mm = (field: string, headerName: string, width = 140): GridSpecColumn => ({field, headerName, width, format: MILLIONS}),
    perShare = (field: string, headerName: string, width = 90): GridSpecColumn => ({field, headerName, width, format: PER_SHARE}),
    dateCol: GridSpecColumn = {field: 'date', headerName: 'Date', width: 120};

// Quarterly estimates aren't in the FMP plan, and annual and quarterly statements must not be mixed
const ANNUAL_ESTIMATES: ResearchDataSource = {dataset: 'analyst_estimate', filters: [{field: 'period', op: 'eq', value: 'annual'}]},
    ANNUAL_INCOME: ResearchDataSource = {dataset: 'income_statement', filters: [{field: 'period', op: 'eq', value: 'FY'}]},
    RATINGS: ResearchDataSource = {dataset: 'fmp_rating'};

const estimateRange = (metric: string): GridSpec => ({
    ...DEFAULT_GRID_SPEC,
    sortBy: NEWEST_FIRST,
    columns: [dateCol, mm(`${metric}Low`, 'Low (MM)', 90), mm(`${metric}Avg`, 'AVG (MM)', 90), mm(`${metric}High`, 'High (MM)', 90)]
});

const estimatesGrid: GridSpec = {
    ...DEFAULT_GRID_SPEC,
    sortBy: NEWEST_FIRST,
    columns: [
        dateCol,
        mm('revenueAvg', 'Revenue (MM)'),
        mm('ebitdaAvg', 'EBITDA (MM)'),
        mm('ebitAvg', 'EBIT (MM)'),
        mm('netIncomeAvg', 'Net Income (MM)'),
        mm('sgaExpenseAvg', 'SG&A Expense (MM)'),
        perShare('epsAvg', 'EPS', 140)
    ]
};

const estimatesEpsGrid: GridSpec = {
    ...DEFAULT_GRID_SPEC,
    sortBy: NEWEST_FIRST,
    columns: [dateCol, perShare('epsLow', 'Low'), perShare('epsAvg', 'AVG'), perShare('epsHigh', 'High')]
};

const incomeStatementGrid: GridSpec = {
    ...DEFAULT_GRID_SPEC,
    sortBy: NEWEST_FIRST,
    columns: [
        {field: 'date', headerName: 'Fiscal Year', width: 100, format: YEAR},
        {
            groupId: 'revenueGroup',
            headerName: 'Revenue',
            children: [mm('revenue', 'Revenue (MM)'), mm('costOfRevenue', 'Cost (MM)'), mm('grossProfit', 'Gross Profit (MM)')]
        },
        {
            groupId: 'expensesGroup',
            headerName: 'Expenses',
            children: [
                mm('researchAndDevelopmentExpenses', 'R&D (MM)'),
                mm('generalAndAdministrativeExpenses', 'Administrative (MM)'),
                mm('sellingAndMarketingExpenses', 'Marketing (MM)'),
                mm('operatingExpenses', 'Operating (MM)')
            ]
        },
        {
            groupId: 'incomeGroup',
            headerName: 'Income',
            children: [
                mm('operatingIncome', 'Operating (MM)'),
                mm('interestIncome', 'Interest (MM)'),
                mm('depreciationAndAmortization', 'Dep/Amort (MM)'),
                mm('netIncome', 'Net (MM)'),
                mm('netIncomeDeductions', 'Deductions (MM)'),
                mm('bottomLineNetIncome', 'Bottom Line (MM)')
            ]
        },
        {field: 'eps', headerName: 'EPS', width: 90, format: PER_SHARE}
    ]
};

const estimatesChart: ChartSpec = {
    ...DEFAULT_CHART_SPEC,
    xField: 'date',
    series: [
        {field: 'revenueAvg', name: 'Revenue'},
        {field: 'ebitdaAvg', name: 'EBITDA'},
        {field: 'ebitAvg', name: 'EBIT'},
        {field: 'netIncomeAvg', name: 'Net Income'},
        {field: 'sgaExpenseAvg', name: 'SG&A Expense'},
        {field: 'epsAvg', name: 'EPS', axis: 'secondary', dashed: true}
    ],
    axes: {
        x: {format: YEAR},
        primary: {title: 'Value (MM)', format: MILLIONS},
        secondary: {title: 'EPS', format: PER_SHARE}
    }
};

const ratingsHistoryChart: ChartSpec = {
    ...DEFAULT_CHART_SPEC,
    xField: 'date',
    series: [
        {field: 'overallScore', name: 'Overall'},
        {field: 'discountedCashFlowScore', name: 'DCF'},
        {field: 'returnOnEquityScore', name: 'RoE'},
        {field: 'returnOnAssetsScore', name: 'RoA'},
        {field: 'debtToEquityScore', name: 'D to E'},
        {field: 'priceToEarningsScore', name: 'PE'}
    ],
    axes: {x: {}, primary: {title: 'Rating'}, secondary: {}}
};

const gradesConsensusChart: ChartSpec = {
    ...DEFAULT_CHART_SPEC,
    type: 'pie',
    layout: 'categories',
    legend: 'none',
    dataLabels: 'name',
    innerSize: 75,
    series: [
        {field: 'strongBuy', color: 'green'},
        {field: 'buy', color: 'teal'},
        {field: 'hold', color: 'yellow'},
        {field: 'sell', color: 'orange'},
        {field: 'strongSell', color: 'red'}
    ],
    axes: {x: {}, primary: {title: 'Analysts'}, secondary: {}}
};

const priceTargetChart: ChartSpec = {
    ...DEFAULT_CHART_SPEC,
    type: 'column',
    layout: 'categories',
    legend: 'none',
    series: [
        {field: 'lastMonthAvgPriceTarget', name: 'Month'},
        {field: 'lastQuarterAvgPriceTarget', name: 'Quarter'},
        {field: 'lastYearAvgPriceTarget', name: 'Year'},
        {field: 'allTimeAvgPriceTarget', name: 'All Time'}
    ],
    axes: {x: {}, primary: {title: 'Price Target', format: PER_SHARE}, secondary: {}}
};

const grid = (id: string, group: string, title: string, source: ResearchDataSource, spec?: GridSpec, width = 14, height = 12): ResearchPreset => ({
    id,
    group,
    title,
    kind: 'grid',
    state: spec ? {source, spec} : {source},
    width,
    height
});

const chart = (id: string, group: string, title: string, source: ResearchDataSource, spec: ChartSpec, width = 14, height = 12): ResearchPreset => ({
    id,
    group,
    title,
    kind: 'chart',
    state: {source, spec},
    width,
    height
});

export const RESEARCH_PRESETS: ResearchPreset[] = [
    grid('estimates', 'Estimates', 'Estimates', ANNUAL_ESTIMATES, estimatesGrid, 22, 14),
    chart('estimatesChart', 'Estimates', 'Estimates (avg)', ANNUAL_ESTIMATES, estimatesChart, 18, 14),
    grid('estimatesRevenue', 'Estimates', 'Revenue', ANNUAL_ESTIMATES, estimateRange('revenue'), 10),
    grid('estimatesEbitda', 'Estimates', 'EBITDA', ANNUAL_ESTIMATES, estimateRange('ebitda'), 10),
    grid('estimatesNetIncome', 'Estimates', 'Net Income', ANNUAL_ESTIMATES, estimateRange('netIncome'), 10),
    grid('estimatesEps', 'Estimates', 'EPS', ANNUAL_ESTIMATES, estimatesEpsGrid, 10),
    grid('ratings', 'Ratings', 'Ratings', {...RATINGS, latest: true, transpose: true}, null, 8),
    chart('ratingsHistory', 'Ratings', 'Historical Ratings', RATINGS, ratingsHistoryChart),
    chart('gradesConsensus', 'Ratings', 'Grades Consensus', {dataset: 'analyst_grades', latest: true}, gradesConsensusChart, 8),
    chart('priceTarget', 'Ratings', 'Price Target', {dataset: 'price_target_summary', latest: true}, priceTargetChart, 10),
    grid('incomeStatement', 'Financials', 'Income Statement', ANNUAL_INCOME, incomeStatementGrid, 40, 14),
    grid('keyMetrics', 'Financials', 'Key Metrics (TTM)', {dataset: 'key_metrics_ttm', latest: true, transpose: true}, null, 10, 16)
];

/** The in-code default dashboard - what a user sees before saving one of their own. Laid out on 40 columns. */
const DEFAULT_LAYOUT: [id: string, x: number, y: number][] = [
    ['estimates', 0, 0],
    ['estimatesChart', 22, 0],
    ['ratings', 0, 14],
    ['ratingsHistory', 8, 14],
    ['gradesConsensus', 22, 14],
    ['priceTarget', 30, 14],
    ['incomeStatement', 0, 26]
];

export function defaultDashboardState(): DashCanvasItemState[] {
    return DEFAULT_LAYOUT.map(([id, x, y]) => {
        const preset = RESEARCH_PRESETS.find(p => p.id === id);
        return {
            viewSpecId: preset.kind,
            title: preset.title,
            layout: {x, y, w: preset.width, h: preset.height},
            state: preset.state
        };
    });
}
